// Translations for the home, Portugal DMC and sample programme pages.
//
// - English is the default and has no URL prefix (/portugal-dmc).
// - Other languages live under their own prefix (/pt/portugal-dmc) so
//   search engines can index each one.
// - Text: src/i18n/locales/<lang>.js. Page/button text is read with $t();
//   lists of content (tours, FAQs, programme days...) with $content().
// - English data in src/data/homepage.js and src/data/programmes.js stays
//   the source of truth; each language file overrides it item by item, so
//   anything not translated yet simply shows in English.

import Vue from "vue";
import VueI18n from "vue-i18n";
import en from "./locales/en";

Vue.use(VueI18n);

export const DEFAULT_LOCALE = "en";

// Order shown in the language switcher
export const LOCALES = [
  { code: "en", name: "English", ogLocale: "en_GB", dateLocale: "en-GB" },
  { code: "pt", name: "Português", ogLocale: "pt_PT", dateLocale: "pt-PT" },
  { code: "es", name: "Español", ogLocale: "es_ES", dateLocale: "es-ES" },
  { code: "de", name: "Deutsch", ogLocale: "de_DE", dateLocale: "de-DE" },
  { code: "fr", name: "Français", ogLocale: "fr_FR", dateLocale: "fr-FR" },
];

export const LOCALE_CODES = LOCALES.map((l) => l.code);

// For route paths: "pt|es|de|fr"
export const PREFIXED_LOCALES = LOCALE_CODES.filter((c) => c !== DEFAULT_LOCALE);

export const i18n = new VueI18n({
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en },
  silentFallbackWarn: true,
});

const loaded = new Set([DEFAULT_LOCALE]);

// Other languages are separate chunks, loaded the first time they're used
export async function loadLocale(code) {
  if (!LOCALE_CODES.includes(code) || loaded.has(code)) return;
  const messages = await import(/* webpackChunkName: "lang-[request]" */ `./locales/${code}.js`);
  i18n.setLocaleMessage(code, messages.default);
  loaded.add(code);
}

// The visitor's language choice, remembered in their browser so they don't
// have to pick it again. (Search engines don't keep it, so they always see
// each address as it is.)
const PREFERENCE_KEY = "guiders.language";

// Crawlers and the build's pre-renderer must always get each address in
// its own language: a remembered language would redirect them (and the
// pre-renderer would then save English pages under /pt/...).
function isAutomatedVisit() {
  if (typeof window === "undefined") return true;
  if (window.__PRERENDER_INJECTED || navigator.webdriver) return true;
  return /bot|crawl|spider|slurp|headless|prerender|lighthouse/i.test(navigator.userAgent || "");
}

export function saveLanguagePreference(code) {
  if (!LOCALE_CODES.includes(code) || isAutomatedVisit()) return;
  try {
    window.localStorage.setItem(PREFERENCE_KEY, code);
  } catch (e) {
    // private mode / storage blocked: just don't remember
  }
}

export function getLanguagePreference() {
  if (isAutomatedVisit()) return null;
  try {
    const code = window.localStorage.getItem(PREFERENCE_KEY);
    return LOCALE_CODES.includes(code) ? code : null;
  } catch (e) {
    return null;
  }
}

// "/pt/portugal-dmc" -> { locale: "pt", path: "/portugal-dmc" }
export function splitLocalePath(path) {
  const match = path.match(new RegExp(`^/(${PREFIXED_LOCALES.join("|")})(?=/|$)`));
  if (!match) return { locale: DEFAULT_LOCALE, path: path || "/" };
  return { locale: match[1], path: path.slice(match[0].length) || "/" };
}

// "/portugal-dmc" + "pt" -> "/pt/portugal-dmc"; "/" + "pt" -> "/pt"
export function localePath(path, code) {
  if (!code || code === DEFAULT_LOCALE) return path;
  return path === "/" ? `/${code}` : `/${code}${path}`;
}

// Read a value (string, list or object) from the current language,
// falling back to English
function getPath(obj, path) {
  return path.split(".").reduce((o, key) => (o == null ? undefined : o[key]), obj);
}

// vue-i18n's public `messages` getter returns a full deep copy on every read,
// so read the stored (reactive) messages directly
function messagesFor(code) {
  return (i18n._vm && i18n._vm.messages[code]) || {};
}

export function content(path, code = i18n.locale) {
  const value = getPath(messagesFor(code), path);
  return value === undefined ? getPath(messagesFor(DEFAULT_LOCALE), path) : value;
}

// English list + translated list (same order) -> merged list.
// Items or fields missing from the translation stay in English.
export function mergeByIndex(base, overrides) {
  if (!Array.isArray(overrides)) return base;
  return base.map((item, i) => {
    const o = overrides[i];
    if (o == null) return item;
    if (typeof item !== "object" || typeof o !== "object") return o;
    return { ...item, ...o };
  });
}

// Deep merge for a programme: arrays of objects (days) merge item by item
export function mergeDeep(base, overrides) {
  if (overrides == null) return base;
  if (Array.isArray(base)) {
    if (!Array.isArray(overrides)) return base;
    return base.map((item, i) =>
      overrides[i] === undefined ? item : mergeDeep(item, overrides[i]),
    );
  }
  if (typeof base === "object" && base !== null && typeof overrides === "object") {
    const result = { ...base };
    Object.keys(overrides).forEach((key) => {
      result[key] = key in base ? mergeDeep(base[key], overrides[key]) : overrides[key];
    });
    return result;
  }
  return overrides;
}

// Helpers available in every component
Vue.mixin({
  methods: {
    // Link to a page in the current language
    $lp(path) {
      return localePath(path, this.$i18n ? this.$i18n.locale : DEFAULT_LOCALE);
    },
    // Site links in the current language: "https://guiders.pt/details/..."
    // -> "/pt/details/..." for pages that exist in every language
    $localUrl(url) {
      if (!url) return url;
      const path = url.replace(/^https?:\/\/(www\.)?guiders\.pt/, "");
      if (!path.startsWith("/")) return url;
      const translatable = /^\/(details\/|programmes\/|portugal-dmc|lisbon-visitor-pass(?![\w-])|$|#)/.test(path);
      return translatable ? localePath(path, this.$i18n ? this.$i18n.locale : DEFAULT_LOCALE) : path;
    },
    $content(path) {
      return content(path, this.$i18n ? this.$i18n.locale : DEFAULT_LOCALE);
    },
    // City / language names: "Lisbon" -> "Lisboa" (falls back to the name)
    $place(name) {
      const map = content("names.places") || {};
      return map[name.trim()] || name.trim();
    },
    $language(name) {
      const map = content("names.languages") || {};
      return map[name.trim()] || name.trim();
    },
  },
});

export function currentLocaleInfo() {
  return LOCALES.find((l) => l.code === i18n.locale) || LOCALES[0];
}
